import React from "react";
import { SecondaryButton } from "@prestosports/commons-ui";
import { getByPath, setSafePath } from "../../../util/object-util";
import FieldMarkersEditor from "../components/field-selectors/FieldMarkersEditor";

// Flag-football extension for the generic Configuration form. Everything the
// settings screens know about flag football lives here — presets, the field
// marker editors and the coupled-value bookkeeping — so Configuration itself
// stays sport-agnostic. Parents pass this object as the `extension` prop when
// the sport is flag football.

const RED_ZONE_TYPE = "redZone";

// Full-field presets applied across the settings form (formik paths). Each
// preset is complete — it sets the first-down style, its zone-to-gain spots
// AND the yard-to-gain chain distance, the field areas, and the default
// yardlines together, so presets are never mixed piecemeal.
// "5v5" per the USA Football Flag 5v5 Tournament Rulebook (25x70yd) and "7v7"
// per the USA Football ADM 7-on-7 Flag Rulebook (30x70yd): both use a 50yd
// field of play + two 10yd end zones, first down only by crossing midfield,
// possession starting at the offense's own 5, and PAT from the 5 (1pt) or 10
// (2pt) — they differ only in field width. Areas are seeded EXCEPT no-run
// zones (the noRunZone type is hidden for now — no rule behavior): a 10yd red
// zone before each goal line, with redZoneLength kept in sync (the red-zone
// stat input). All values stay editable after applying.
const USA_FLAG_FIELD_PRESET = {
	"general.fieldLength": 50,
	"general.endZoneLength": 10,
	"general.firstDownFlagStyle": 1,
	"general.firstDownYardSet": [25],
	"general.customFirstDownYardSet": true,
	"general.firstDownYards": 10,
	"general.fieldAreaSet": [
		{ start: 0, end: 10, type: "redZone" },
		{ start: 40, end: 50, type: "redZone" }
	],
	"general.redZoneLength": 10,
	"spots.driveStartingYardline": 5,
	"spots.spotsPatScoreOption1": 5,
	"spots.spotsPatScoreOption2": 10
};

const FIELD_PRESETS = {
	"5v5": {
		...USA_FLAG_FIELD_PRESET,
		"general.fieldWidth": 25
	},
	"7v7": {
		...USA_FLAG_FIELD_PRESET,
		"general.fieldWidth": 30
	},
	// Common men's-league 9v9 spec (no official USA Football 9v9 rulebook):
	// 80yd field of play x 40yd + 10yd end zones, zone-to-gain every 20yds
	// (goal line comes from the core fallback when no line remains ahead),
	// own-5 drive start, PAT from the 3 (1pt) or 10 (2pt).
	"9v9": {
		"general.fieldLength": 80,
		"general.fieldWidth": 40,
		"general.endZoneLength": 10,
		"general.firstDownFlagStyle": 1,
		"general.firstDownYardSet": [20, 40, 60],
		"general.customFirstDownYardSet": true,
		"general.firstDownYards": 10,
		"general.fieldAreaSet": [
			{ start: 0, end: 10, type: "redZone" },
			{ start: 70, end: 80, type: "redZone" }
		],
		"general.redZoneLength": 10,
		"spots.driveStartingYardline": 5,
		"spots.spotsPatScoreOption1": 3,
		"spots.spotsPatScoreOption2": 10
	}
};

const FIELD_PRESET_OPTIONS = [
	{ id: "5v5", label: "5v5 field" },
	{ id: "7v7", label: "7v7 field" },
	{ id: "9v9", label: "9v9 field" }
];

// Apply the whole preset as ONE setValues call. Per-path setFieldValue
// would trigger a full-form re-render AND a complete Yup validation pass
// per field (unbatched on React 16) — with ~13 preset fields that froze
// the settings screen for seconds.
const applyFieldPreset = (presetId, formApi) => {
	const preset = FIELD_PRESETS[presetId];
	if (preset) {
		let nextValues = formApi.values;
		Object.entries(preset).forEach(([path, presetValue]) => {
			nextValues = setSafePath(nextValues, path.split("."), presetValue);
		});
		formApi.setValues(nextValues);
	}
};

// Changing the field length invalidates yard-based marker config: spots
// beyond the new length would clamp onto the ruler edge and fail
// validation on save. Sanitize the coupled values in the same update —
// drop out-of-range first-down spots, clamp/drop field areas, clamp the
// red zone length.
const onFieldLengthChange = (newLength, formApi) => {
	// While the input is being cleared/typed the value can be NaN or 0;
	// just store it and skip marker recompute (Array(NaN) would throw).
	if (!Number.isFinite(newLength) || newLength <= 0) {
		formApi.setFieldValue("general.fieldLength", newLength);
	} else {
		let next = setSafePath(formApi.values, ["general", "fieldLength"], newLength);

		// Changing the field length invalidates hand-authored spots (a
		// midfield-25 set for a 50yd field is meaningless on 40yd), so
		// always recompute the uniform zone-to-gain set for the new
		// length — matching core's game-start formula (multiples of
		// fieldLength / (teamZoneQty * 2), fractional boundaries dropped)
		// — and clear the custom flag. Presets set length and spots
		// together in one update, so they are unaffected.
		const spots = getByPath(formApi.values, "general.firstDownYardSet");
		if (Array.isArray(spots)) {
			const teamZoneQty = getByPath(formApi.values, "general.teamZoneQty") || 2;
			const zoneSize = newLength / (teamZoneQty * 2);
			const uniform = Array.from(Array(newLength + 1).keys()).filter(
				(k) => k !== 0 && k % zoneSize === 0
			);
			next = setSafePath(next, ["general", "firstDownYardSet"], uniform);
			next = setSafePath(next, ["general", "customFirstDownYardSet"], false);
		}

		// Reposition areas for the new length. An area anchored to the
		// far goal line (end === old length, e.g. a preset's far red
		// zone) slides to keep touching the new goal line at the same
		// width; others keep their start and clamp their end. Areas that
		// can't fit at all are dropped.
		const oldLength = getByPath(formApi.values, "general.fieldLength");
		const areas = getByPath(formApi.values, "general.fieldAreaSet");
		if (Array.isArray(areas)) {
			const repositioned = areas
				.map((a) => {
					let mapped = a;
					if (a.end === oldLength) {
						const width = a.end - a.start;
						mapped = {
							...a,
							start: Math.max(0, newLength - width),
							end: newLength
						};
					} else {
						mapped = { ...a, end: Math.min(a.end, newLength) };
					}
					return mapped;
				})
				.filter((a) => a.start < a.end);
			next = setSafePath(next, ["general", "fieldAreaSet"], repositioned);
		}

		const redZoneLength = getByPath(formApi.values, "general.redZoneLength");
		if (typeof redZoneLength === "number" && redZoneLength > newLength) {
			next = setSafePath(next, ["general", "redZoneLength"], newLength);
		}

		formApi.setValues(next);
	}
};

// Persist marker edits, then keep the coupled scalar config in sync:
// editing the first-down spots flags the set as crew-authored (so core
// stops recomputing uniform zones), and a goal-line red-zone area drives
// redZoneLength (the only area type that feeds stats).
const onMarkersChange = (element, next, formApi) => {
	formApi.setFieldValue(element.key, next);
	if (element.key.endsWith("firstDownYardSet")) {
		formApi.setFieldValue("general.customFirstDownYardSet", true);
	}
	if (element.key.endsWith("fieldAreaSet")) {
		const redZone = next.find((a) => a.type === RED_ZONE_TYPE && a.start === 0);
		if (redZone) {
			formApi.setFieldValue("general.redZoneLength", redZone.end);
		}
	}
};

const flagFootballConfiguration = {
	// Field presets rewrite ~13 fields across ALL sections (field size,
	// first-down style/spots, areas, PAT/drive spots), so they surface in the
	// General section header — screen-level scope — not inside the areas
	// editor. Hidden in previews, where applying a preset makes no sense.
	sectionActions: (sectionKey, formApi) => {
		let actions = null;
		if (sectionKey === "general" && !formApi.preview) {
			actions = FIELD_PRESET_OPTIONS.map((preset) => (
				<SecondaryButton
					key={preset.id}
					id={`button-field-preset-${preset.id}`}
					disabled={formApi.disabled}
					onClick={() => applyFieldPreset(preset.id, formApi)}
					label={preset.label}
				/>
			));
		}
		return actions;
	},

	componentRenderers: {
		fieldMarkers: (element, formApi) => (
			<FieldMarkersEditor
				key={element.key}
				value={getByPath(formApi.values, element.key) || []}
				fieldLength={getByPath(formApi.values, "general.fieldLength")}
				markerKind={element.markerKind}
				areaTypes={element.areaTypes || []}
				disabled={formApi.disabled || element.disabled || formApi.preview}
				onChange={(next) => onMarkersChange(element, next, formApi)}
				firstDownStyle={getByPath(formApi.values, "general.firstDownFlagStyle")}
				yardsToGain={getByPath(formApi.values, "general.firstDownYards")}
				onYardsToGainChange={(yards) =>
					formApi.setFieldValue("general.firstDownYards", yards)
				}
			/>
		)
	},

	// Field length changes must sanitize the coupled marker config, so claim
	// that one path instead of letting formik's plain handleChange store it.
	interceptChange: (path, value, formApi) => {
		let handled = false;
		if (path === "general.fieldLength") {
			onFieldLengthChange(Number(value), formApi);
			handled = true;
		}
		return handled;
	},

	// Marker rulers span the full settings row; the yard-to-gain first-down
	// field renders as a plain distance input, so keep it inline with the
	// other general fields.
	isFullWidth: (element, formApi) => {
		const isYardToGainSpots =
			element.key.endsWith("firstDownYardSet") &&
			getByPath(formApi.values, "general.firstDownFlagStyle") <= 0;
		return element.component === "fieldMarkers" && !isYardToGainSpots;
	}
};

export default flagFootballConfiguration;
